import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail } from 'lucide-react';
import { inventoryApi } from '../../services/inventoryApi';
import type { DrillDownContext, EmailLogEntry, EmailLogRecipient } from '../../types/inventory';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    item: DrillDownContext;
}

function formatDateTime(dateStr: string): string {
    const d = new Date(dateStr);
    return `${d.toLocaleDateString('de-CH')} ${d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}`;
}

function recipientLabel(r: EmailLogRecipient): string {
    return r.name?.trim() || r.email || '';
}

function recipientList(recipients: EmailLogRecipient[]): string {
    return recipients.map(recipientLabel).filter(Boolean).join(', ');
}

export function EmailLogModal({ isOpen, onClose, item }: Props) {
    const [emails, setEmails] = useState<EmailLogEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        setError(null);

        inventoryApi.getEmailLog(item.ItemID)
            .then(data => setEmails(data.emails))
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [isOpen, item.ItemID]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            {/* Modal */}
            <div className="relative z-10 mx-4 flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <div>
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                            <Mail className="h-5 w-5 text-emerald-600" />
                            Sent Emails
                        </h2>
                        <p className="mt-0.5 text-sm text-gray-500">
                            {item.ProductName} ({item.ProductCode}) &middot; {item.CompanyCode}
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto">
                    {loading && (
                        <div className="flex items-center justify-center py-16">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        </div>
                    )}

                    {error && (
                        <div className="m-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {!loading && !error && emails.length === 0 && (
                        <div className="py-16 text-center text-sm text-gray-500">
                            No emails have been sent for this item yet.
                        </div>
                    )}

                    {!loading && !error && emails.length > 0 && (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Date</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">From</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Recipients</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Subject</th>
                                </tr>
                            </thead>
                            <tbody>
                                {emails.map(e => {
                                    const cc = recipientList(e.cc);
                                    return (
                                        <tr key={e.id} className="border-b border-gray-100 align-top hover:bg-gray-50">
                                            <td className="whitespace-nowrap px-4 py-2 text-gray-600">{formatDateTime(e.sentAt)}</td>
                                            <td className="px-4 py-2 text-gray-700">{e.sentByEmail}</td>
                                            <td className="px-4 py-2 text-gray-700">
                                                <div>{recipientList(e.to) || '—'}</div>
                                                {cc && <div className="mt-0.5 text-xs text-gray-400">CC: {cc}</div>}
                                            </td>
                                            <td className="px-4 py-2 text-gray-900">{e.subject}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
